import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';

import { theme } from '../../styles/theme';

import { MapInfoCard } from './MapInfoCard';

type Props = {
  closeCard: (value: boolean) => void;
  title: string;
  description: string;
  url: string;
  date: string | null;
  country: string;
  city: string;
  studyName: string;
  studyAcronym: string;
  expand: boolean | null;
  handleExpand: (value: boolean | null) => void;
  isExternal: boolean;
  onStudyClick?: () => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
};

export const CaseStudyCard: FC<Props> = ({
  closeCard,
  title,
  description,
  url,
  date,
  country,
  city,
  studyName,
  studyAcronym,
  expand,
  handleExpand,
  isExternal,
  onStudyClick,
  onNavigatePrevious,
  onNavigateNext,
}) => {
  const { t } = useTranslation();

  const handleViewCaseStudy = () => {
    window.open(url, '_blank');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('caseStudyCard.noDate', 'Date not available');

    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const headerFields = [
    {
      label: t('caseStudyCard.type', 'CASE STUDY'),
      value: title,
    },
    {
      label: t('caseStudyCard.linkedStudy', 'Linked Study'),
      value: studyAcronym,
      onClick: onStudyClick,
    },
    ...(date
      ? [
          {
            label: t('caseStudyCard.published', 'Published'),
            value: formatDate(date),
          },
        ]
      : []),
  ];

  const actionButton = (
    <Button
      variant="contained"
      onClick={handleViewCaseStudy}
      endIcon={<OpenInNewIcon />}
      size="small"
      sx={{
        backgroundColor: theme.colors['red-55'],
        '&:hover': {
          backgroundColor: '#c55d59',
        },
      }}
    >
      {t('caseStudyCard.viewCaseStudy', 'View case study')}
      {isExternal && ` (${t('caseStudyCard.external', 'External')})`}
    </Button>
  );

  return (
    <MapInfoCard
      closeCard={closeCard}
      headerFields={headerFields}
      description={description}
      actionButton={actionButton}
      expand={expand}
      handleExpand={handleExpand}
      onNavigatePrevious={onNavigatePrevious}
      onNavigateNext={onNavigateNext}
    />
  );
};
